import { TextField } from "@mui/material";
import type { ModalComponentProps } from "../../../types/modal.types";
import ConfirmModal from "../confirm.modal";
import { cn } from "../../../utils/cn.utils";
import type { StoreDateLogItem, StoreDetailResponse } from "../../../lib/apis/dtos/response/store.response";
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useStoreDateLog } from "../../../lib/apis/hooks/store.hook";
import { formatDateTime } from "../../../utils/date.utils";

interface BranchExpirationModalProps extends ModalComponentProps {
  store: StoreDetailResponse
  /** 이력 조회용 지점 id */
  storeId?: string | number
  click: (form: { startDate: string; endDate: string }) => void
}

const LOG_PAGE_SIZE = 20;

/** 변경 구분 — 서버 logType 을 그대로 쓰되 사유(memo)가 있으면 덧붙인다 */
const changeTypeLabel = ({ logType, memo }: StoreDateLogItem) => {
  const type = logType?.trim() || "-";
  return memo?.trim() ? `${type} (${memo.trim()})` : type;
};

function HistoryRow({ item }: { item: StoreDateLogItem }) {
  return (
    <tr className="content-table-tr">
      <td className="p-4 text-[14px] whitespace-nowrap">
        {item.time ? formatDateTime(item.time, "YYYY-MM-DD HH:mm") : "-"}
      </td>
      <td className="p-4 text-[14px]">{item.startDayDiff || "-"}</td>
      <td className="p-4 text-[14px]">{item.endDayDiff || "-"}</td>
      <td className="p-4 text-[14px] whitespace-nowrap">{changeTypeLabel(item)}</td>
      <td className="p-4 text-[14px]">{item.userName || "-"}</td>
    </tr>
  );
}

const HEADER_ROW = [
  { label: "변경 일시", key: "changeDate" },
  { label: "시작일 변경사항", key: "before" },
  { label: "종료일 변경사항", key: "after" },
  { label: "변경 구분", key: "changeType" },
  { label: "변경자", key: "changer" },
]

export default function BranchExpirationModal(props: BranchExpirationModalProps) {
  const { id, onClose, store, storeId, click } = props;

  const { data: historyData, isSuccess: isHistorySuccess } = useQuery({
    ...useStoreDateLog(storeId ?? "", { page: 0, size: LOG_PAGE_SIZE }),
  });
  const history = historyData?.result?.content ?? [];

  const [form, setForm] = React.useState({
    startDate: store.startDate || "",
    endDate: store.endDate || "",
  })

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const id = e.target.id;
    const value = e.target.value.replace(/[^0-9]/g, ""); // 숫자만 남기기
    let formatted = value;

    if (value.length <= 4) {
      formatted = value;
    } else if (value.length <= 6) {
      formatted = `${value.slice(0, 4)}-${value.slice(4)}`;
    } else {
      formatted = `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`;
    }

    setForm((prev) => ({
      ...prev,
      [id]: formatted,
    }));
  };

  return (
    <ConfirmModal
      className="w-[1200px] px-6"
      id={id}
      onClose={onClose} title={`${store.storeName} 사용기간 변경`}
      rightButton={{
        title: "사용기간 저장",
        variant: "primary",
        onClick: () => {
          click(form);
          onClose?.();
        }
      }}
    >
      <section className="pt-4">
        <table className="w-full">
          <tr className="content-table-tr">
            <td className={cn("content-table-th", "p-0")}>사용기간 선택</td>
            <td className={cn("content-table-td")}
            >
              <div className="flex-row gap-4 h-full">
                <TextField
                  label="시작일"
                  id="startDate"
                  value={form.startDate}
                  onChange={handleDateChange}
                  placeholder="YYYY-MM-DD"
                  inputProps={{ maxLength: 10 }} // 최대 길이 제한
                  slotProps={{
                    inputLabel: { shrink: true },
                    input: {
                      sx: { height: "40px" }
                    }
                  }}
                />
                <p>-</p>
                <TextField
                  label="종료일"
                  id="endDate"
                  value={form.endDate}
                  onChange={handleDateChange}
                  placeholder="YYYY-MM-DD"
                  inputProps={{ maxLength: 10 }} // 최대 길이 제한
                  slotProps={{
                    inputLabel: { shrink: true },
                    input: {
                      sx: { height: "40px" }
                    }
                  }}
                />
              </div>
            </td>
          </tr>
        </table>

        <p className="p-4 mt-4 text-style-title-03 font-medium">사용기간 변경 이력</p>
        <table className="w-full">
          <thead>
            <tr className="content-table-tr">
              {HEADER_ROW.map((row) => (
                <th className="font-normal text-left p-4" key={row.key}>{row.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {history.map((item, index) => (
              <HistoryRow key={`${item.time}_${index}`} item={item} />
            ))}
            {isHistorySuccess && history.length === 0 && (
              <tr>
                <td className="p-4 text-[14px] text-black/40" colSpan={HEADER_ROW.length}>변경 이력이 없습니다.</td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </ConfirmModal>
  );
}